import { useEffect, useRef, useState } from 'react';
import { useWeb3Auth } from '../auth/Web3AuthContext';
import RPC from '../auth/viemRPC';
import { handleDrawEntry } from '../components/handleDrawEntry';
import { getWalletRecipientAddress } from '../config/addresses';
import { serverAxios } from '../config';
import { getGasBufferEth } from '../config/gas';
import { checkEnterDrawStatus } from '../firebase';

interface AutomatedDrawEntryState {
    isProcessing: boolean;
    lastResult?: {
        success: boolean;
        error?: string;
        transactionHash?: string;
    };
}

export const useAutomatedDrawEntry = (
    userEmail: string | undefined,
    isAuthenticated: boolean,
    canEnterDraw: boolean
) => {
    const WITHDRAW_PROGRESS_KEY = 'withdrawInProgress';
    const { provider, isSessionReady, web3auth } = useWeb3Auth();
    const [state, setState] = useState<AutomatedDrawEntryState>({
        isProcessing: false
    });
    const isAttemptInFlightRef = useRef(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingStartRef = useRef<number | null>(null);
    const hasCompletedRef = useRef(false);
    const hasSessionFailureRef = useRef(false);
    const lastUserEmailRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const isWithdrawInProgress = () => {
            try {
                return localStorage.getItem(WITHDRAW_PROGRESS_KEY) === '1';
            } catch {
                return false;
            }
        };

        if (isWithdrawInProgress()) {
            return;
        }

        if (!userEmail || !isAuthenticated || !provider || !canEnterDraw) {
            hasCompletedRef.current = false;
            hasSessionFailureRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            pollingStartRef.current = null;
            return;
        }

        const POLL_INTERVAL_MS = 15000;
        const MAX_POLL_DURATION_MS = 10 * 60 * 1000;
        let timeoutId: NodeJS.Timeout;

        if (lastUserEmailRef.current !== userEmail) {
            hasCompletedRef.current = false;
            hasSessionFailureRef.current = false;
            lastUserEmailRef.current = userEmail;
        }

        const checkAndProcessDrawEntry = async () => {
            if (isWithdrawInProgress()) {
                return;
            }
            if (isAttemptInFlightRef.current || hasCompletedRef.current || hasSessionFailureRef.current) {
                return;
            }

            if (pollingStartRef.current && Date.now() - pollingStartRef.current > MAX_POLL_DURATION_MS) {
                return;
            }

            try {
                isAttemptInFlightRef.current = true;
                const session = await isSessionReady();
                if (!session.ready || !session.provider) {
                    const reason = session.reason || 'Session expired. Please sign in again to continue.';
                    console.warn('Automated draw entry blocked by session readiness:', reason);
                    hasSessionFailureRef.current = true;
                    hasCompletedRef.current = true;
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    setState(prev => ({
                        ...prev,
                        isProcessing: false,
                        lastResult: { success: false, error: reason }
                    }));
                    return;
                }

                // Check if user can enter draw
                const canEnterDraw = await checkEnterDrawStatus(userEmail);
                if (!canEnterDraw) {
                    return;
                }

                setState(prev => ({ ...prev, isProcessing: true }));

                // Create RPC instance and process draw entry
                const rpc = new RPC(session.provider);
                const RECIPIENT_ADDRESS = getWalletRecipientAddress();

                // Check USDC balance and native ETH for gas
                const usdcBalance = await rpc.getBalance();
                const nativeBalance = await rpc.getNativeBalance();

                const usdcNum = Number(usdcBalance);
                if (!isFinite(usdcNum) || usdcNum <= 0) {
                    setState(prev => ({
                        ...prev,
                        isProcessing: false,
                        lastResult: { success: false, error: 'Insufficient USDC balance' }
                    }));
                    return;
                }

                const minRequired = getGasBufferEth();

                const nativeNum = Number(nativeBalance);
                if (!isFinite(nativeNum) || nativeNum < minRequired) {
                    // Request gas top-up from backend
                    try {
                        const addresses = await rpc.getAccounts();
                        const walletAddress = addresses?.[0];
                        if (walletAddress) {
                            let idToken: string | undefined;
                            try {
                                const tokenInfo = await web3auth?.getIdentityToken();
                                idToken = tokenInfo?.idToken;
                            } catch (tokenError) {
                                console.warn('Unable to obtain Web3Auth ID token for gas top-up:', tokenError);
                            }

                            if (!idToken) {
                                console.warn('Skipping gas top-up request: no Web3Auth ID token available');
                            } else {
                                // Request top-up but don't fail if it fails - next poll cycle will retry
                                await serverAxios
                                    .post(
                                        '/gas-topup',
                                        { walletAddress },
                                        { headers: { Authorization: `Bearer ${idToken}` } }
                                    )
                                    .then(response => {
                                        // Detects the "stuck range" from P1-2: if the backend's
                                        // threshold is lower than ours, wallets between the two
                                        // values will request a top-up forever and never get one.
                                        const { threshold, target } = response.data ?? {};
                                        if (typeof threshold === 'number' && threshold < minRequired) {
                                            console.warn(
                                                `Gas top-up misconfiguration: backend threshold (${threshold} ETH) is below ` +
                                                `the frontend gas buffer (${minRequired} ETH, VITE_GAS_BUFFER_ETH). Wallets in ` +
                                                `between will never qualify for a top-up. target=${target}`
                                            );
                                        }
                                    })
                                    .catch(err => {
                                        console.warn('Gas top-up request failed:', err);
                                    });
                            }
                        }
                    } catch (err) {
                        console.warn('Error requesting gas top-up:', err);
                    }

                    setState(prev => ({
                        ...prev,
                        isProcessing: false,
                        lastResult: {
                            success: false,
                            error: `Insufficient ETH for gas. Have: ${nativeBalance} ETH, need: ${minRequired} ETH. Gas top-up requested.`
                        }
                    }));
                    return;
                }

                const result = await handleDrawEntry(
                    userEmail,
                    rpc,
                    RECIPIENT_ADDRESS,
                    usdcBalance
                );

                setState(prev => ({
                    ...prev,
                    isProcessing: false,
                    lastResult: result
                }));

                if (result.success) {
                    hasCompletedRef.current = true;
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    // Optional: You can add toast notifications here later
                } else {
                    console.error('Failed to enter draw automatically:', result.error);
                    if (result.error?.toLowerCase().includes('sign in again')) {
                        hasSessionFailureRef.current = true;
                        hasCompletedRef.current = true;
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                    }
                }

            } catch (error: any) {
                console.error('Error in automated draw entry:', error);
                const message = error.message || 'Unknown error occurred';
                if (
                    /session|provider|disconnected|login|sign in/i.test(message)
                ) {
                    hasSessionFailureRef.current = true;
                    hasCompletedRef.current = true;
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                }
                setState(prev => ({
                    ...prev,
                    isProcessing: false,
                    lastResult: { success: false, error: message }
                }));
            } finally {
                isAttemptInFlightRef.current = false;
            }
        };

        // Run check with a small delay to ensure all authentication is complete
        timeoutId = setTimeout(() => {
            if (!pollingStartRef.current) {
                pollingStartRef.current = Date.now();
            }
            checkAndProcessDrawEntry();
        }, 1000);

        if (!intervalRef.current) {
            intervalRef.current = setInterval(() => {
                if (!pollingStartRef.current) {
                    pollingStartRef.current = Date.now();
                }
                checkAndProcessDrawEntry();
            }, POLL_INTERVAL_MS);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            pollingStartRef.current = null;
        };
    }, [userEmail, isAuthenticated, provider, canEnterDraw, isSessionReady, web3auth]);

    return state;
}; 